import React, { useEffect, useState } from 'react';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { listWalletTransactions } from '../utils/secureFunctions';

interface WalletTransaction {
  id: string;
  type: 'credit' | 'debit' | 'transfer';
  direction: 'in' | 'out' | 'self';
  currency: string;
  amountMinor: string;
  memo?: string | null;
  createdAt: string;
}

const WalletTransactions: React.FC = () => {
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth.currentUser) {
      navigate('/login');
      return;
    }
    loadTransactions();
  }, [navigate]);

  const loadTransactions = async () => {
    try {
      const res = await listWalletTransactions({ limit: 100 });
      setTransactions(
        res.transactions.map((t) => ({
          id: t.id,
          type: t.type,
          direction: t.direction,
          currency: t.currency,
          amountMinor: t.amountMinor,
          memo: t.memo,
          createdAt: t.createdAt,
        }))
      );
    } catch (error) {
      console.error('Failed to load transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (tx: WalletTransaction) => {
    const currency = String(tx.currency || '').toUpperCase();
    const minor = Number.parseInt(String(tx.amountMinor || '0'), 10);
    const sign = tx.type === 'credit' || tx.direction === 'in' ? '+' : tx.type === 'debit' || tx.direction === 'out' ? '-' : '';
    if (currency === 'USD') {
      const usd = Number.isFinite(minor) ? (minor / 100) : 0;
      return `${sign}$${usd.toFixed(2)}`;
    }
    if (!Number.isFinite(minor)) {
      return `${sign}${currency} 0`;
    }
    return `${sign}${currency} ${minor}`;
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Type', 'Amount (USD)', 'Provider', 'Reference', 'Ad ID', 'Interaction', 'Placement'];
    const rows = transactions.map(tx => [
      new Date(tx.createdAt).toLocaleString() || 'N/A',
      tx.type,
      formatAmount(tx),
      '',
      '',
      '',
      '',
      ''
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wallet-transactions-${new Date().toISOString()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div style={styles.container} />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Wallet Transactions</h1>
        <button style={styles.exportBtn} onClick={exportToCSV}>
          Export CSV
        </button>
      </div>

      {transactions.length === 0 ? (
        <div style={styles.empty}>No transactions yet</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.headerRow}>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.th}>Details</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => (
                <tr key={tx.id} style={styles.row}>
                  <td style={styles.td}>
                    {new Date(tx.createdAt).toLocaleString() || 'N/A'}
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.badge,
                      backgroundColor: tx.type === 'credit' ? '#4caf50' : tx.type === 'debit' ? '#f44336' : '#6b7280'
                    }}>
                      {(tx.type + (tx.type === 'transfer' ? `_${tx.direction}` : '')).toUpperCase()}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      color: tx.type === 'credit' ? '#4caf50' : tx.type === 'debit' ? '#f44336' : '#6b7280',
                      fontWeight: 'bold'
                    }}>
                      {formatAmount(tx)}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div>
                      <div><strong>Currency:</strong> {String(tx.currency || '').toUpperCase()}</div>
                      <div><strong>Memo:</strong> {tx.memo || 'N/A'}</div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '30px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#333',
  },
  exportBtn: {
    padding: '10px 20px',
    backgroundColor: '#2196f3',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  loading: {
    textAlign: 'center',
    padding: '50px',
    color: '#666',
  },
  empty: {
    textAlign: 'center',
    padding: '50px',
    color: '#999',
    fontSize: '18px',
  },
  tableContainer: {
    overflowX: 'auto',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  headerRow: {
    backgroundColor: '#f5f5f5',
  },
  th: {
    padding: '15px',
    textAlign: 'left',
    fontWeight: 'bold',
    color: '#555',
    borderBottom: '2px solid #ddd',
  },
  row: {
    borderBottom: '1px solid #eee',
  },
  td: {
    padding: '15px',
    color: '#333',
  },
  badge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 'bold',
    color: 'white',
  },
};

export default WalletTransactions;
